const FLOA_WIDGET_SCRIPT_ID = 'floa-widget-script';

let floaWidgetScriptPromise: Promise<void> | null = null;

export type FloaWidgetConfig = {
  amount: string;
  country: 'FR';
  custom_selector?: string;
  locale: 'fr';
  maxAmount?: Record<string, number>;
  offers: string[];
  pnf: string;
  theme?: 'brand';
};

const waitForExistingScript = (script: HTMLScriptElement) => {
  return new Promise<void>((resolve, reject) => {
    if (script.dataset.loaded === 'true' || window.initFloaWidget) {
      resolve();
      return;
    }

    const handleLoad = () => {
      script.dataset.loaded = 'true';
      cleanup();
      resolve();
    };

    const handleError = () => {
      cleanup();
      reject(new Error('Unable to load FLOA widget script.'));
    };

    const cleanup = () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);
  });
};

export const loadFloaWidgetScript = () => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (window.initFloaWidget) {
    return Promise.resolve();
  }

  if (floaWidgetScriptPromise) {
    return floaWidgetScriptPromise;
  }

  floaWidgetScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(FLOA_WIDGET_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      waitForExistingScript(existingScript).then(resolve).catch((error) => {
        floaWidgetScriptPromise = null;
        reject(error);
      });
      return;
    }

    const script = document.createElement('script');
    script.id = FLOA_WIDGET_SCRIPT_ID;
    script.type = 'module';
    script.src = import.meta.env.VITE_APP_FLOAT_WIDGET_URL;
    script.async = true;

    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      resolve();
    }, { once: true });

    script.addEventListener('error', () => {
      floaWidgetScriptPromise = null;
      reject(new Error('Unable to load FLOA widget script.'));
    }, { once: true });

    document.head.appendChild(script);
  });

  return floaWidgetScriptPromise;
};
